import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Check, CloudSun, UserX, Video, X, UserPlus } from "lucide-react";
import {
  ADM_MAX_VISITS_PER_SLOT,
  canScheduleAnotherVisitInSlot,
  resolveAppointmentPropertyId,
} from "../../scheduleSlotVisitLimit";
import ModalSkeleton from "../../../../components/ModalSkeleton";
import { cn } from "../../../../utils/cn";
import { VisitCardCreatedBadge } from "../../../../components/ModalConsult/VisitScheduleCardInfo";
import { useAuth } from "../../../../hooks/auth";
import ScheduleDetailPanelBody from "./ScheduleDetailPanelBody";
import { useScheduleDetailAppointments } from "./useScheduleDetailAppointments";
import { formatScheduleDetailHeader } from "../../agendaDateFormat";
import { VisitCreateCardButton } from "../../../../components/ModalConsult/VisitScheduleCardInfo";
import { updateScheduleVisitOutcome, type ScheduleVisitOutcome } from "../../../../services/scheduleVisitOutcome";
import { extractApiErrorMessage } from "../../../../utils/scheduleLockPolicy";
import { useToast } from "../../../../hooks/toast";
import {
  SCHEDULE_DETAIL_BACKDROP_Z_INDEX,
  SCHEDULE_DETAIL_DESKTOP_MIN_WIDTH_PX,
  SCHEDULE_DETAIL_SIDEBAR_WIDTH_PX,
  SCHEDULE_DETAIL_SIDEBAR_Z_INDEX,
  SCHEDULE_DETAIL_TRANSITION_MS,
  applyScheduleDetailSidebarBodyOffset,
} from "./scheduleDetailPanelLayout";

export type ScheduleDetailPanelParam = {
  scheduleParam: string;
  targetScheduleId?: number;
};

type ScheduleDetailPanelProps = {
  open: boolean;
  param: ScheduleDetailPanelParam | null;
  onClose: () => void;
  onDemarcate: (scheduleId: number, scheduleDate: string) => void;
  onUpdate: (
    scheduleId: number,
    scheduleParam: string,
    videoCall: boolean
  ) => void;
  onVisitOutcomeUpdated?: () => void;
  /** Books another client in this hour, for the same property (ADM only). */
  onScheduleAnother?: (scheduleParam: string, propertyId: number | null) => void;
  weatherHint?: string | null;
};

function useDesktopSidebarLayout() {
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const media = window.matchMedia(
      `(min-width: ${SCHEDULE_DETAIL_DESKTOP_MIN_WIDTH_PX}px)`
    );
    const sync = () => setIsDesktop(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  return isDesktop;
}

const ScheduleDetailPanel: React.FC<ScheduleDetailPanelProps> = ({
  open,
  param,
  onClose,
  onDemarcate,
  onUpdate,
  onVisitOutcomeUpdated,
  onScheduleAnother,
  weatherHint,
}) => {
  const { user } = useAuth();
  const canManageSchedules = user?.role !== "consultant";
  const { addToast } = useToast();
  const isDesktop = useDesktopSidebarLayout();
  const [shouldRender, setShouldRender] = useState(open);
  const [isPanelVisible, setIsPanelVisible] = useState(false);

  const scheduleParam = param?.scheduleParam ?? "";
  const {
    appointments,
    currentAppointment,
    currentIndex,
    setCurrentIndex,
    isFetching,
    cardStatus,
    setCardStatusOverride,
  } = useScheduleDetailAppointments({
    open,
    scheduleParam,
    targetScheduleId: param?.targetScheduleId,
  });
  const [savingOutcome, setSavingOutcome] = useState<ScheduleVisitOutcome | null>(null);

  function isVisitDue(date: string) {
    const normalized = String(date).trim().replace(" ", "T");
    const withTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(normalized)
      ? normalized
      : `${normalized}-03:00`;
    const scheduledAt = new Date(withTimezone);
    return Number.isFinite(scheduledAt.getTime()) && scheduledAt.getTime() <= Date.now();
  }

  async function handleOutcome(outcome: ScheduleVisitOutcome) {
    if (!currentAppointment || savingOutcome) {
      return;
    }

    setSavingOutcome(outcome);
    try {
      await updateScheduleVisitOutcome([currentAppointment.schedule.id], outcome);
      addToast({
        type: "success",
        title: outcome === "completed" ? "Visita marcada como realizada" : "Visita marcada como não compareceu",
      });
      onVisitOutcomeUpdated?.();
      onClose();
    } catch (error: unknown) {
      addToast({
        type: "error",
        title: extractApiErrorMessage(error) ?? "Não foi possível atualizar o resultado da visita.",
      });
    } finally {
      setSavingOutcome(null);
    }
  }

  const clientPagination = useMemo(() => {
    if (appointments.length <= 1) {
      return null;
    }

    return {
      currentIndex,
      total: appointments.length,
      onPrevious: () =>
        setCurrentIndex((prev) =>
          prev === 0 ? appointments.length - 1 : prev - 1
        ),
      onNext: () =>
        setCurrentIndex((prev) =>
          prev === appointments.length - 1 ? 0 : prev + 1
        ),
    };
  }, [appointments.length, currentIndex, setCurrentIndex]);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      const frame = window.requestAnimationFrame(() => {
        setIsPanelVisible(true);
      });
      return () => window.cancelAnimationFrame(frame);
    }

    setIsPanelVisible(false);
    const timeout = window.setTimeout(() => {
      setShouldRender(false);
    }, SCHEDULE_DETAIL_TRANSITION_MS);

    return () => window.clearTimeout(timeout);
  }, [open]);

  useLayoutEffect(() => {
    const reserveSpace = shouldRender && isDesktop;
    applyScheduleDetailSidebarBodyOffset(reserveSpace);

    return () => {
      applyScheduleDetailSidebarBodyOffset(false);
    };
  }, [shouldRender, isDesktop]);

  useEffect(() => {
    if (!open || isDesktop) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isDesktop, onClose]);

  if (!shouldRender || !param || typeof document === "undefined") {
    return null;
  }

  const panel = (
    <aside
      className={cn(
        "fixed inset-y-0 right-0 flex h-dvh flex-col overflow-hidden bg-[#F8FAFC]",
        "border-l border-[#E2E8F0]",
        "transition-transform ease-out",
        isDesktop
          ? "shadow-[-4px_0_12px_rgba(15,23,42,0.05)]"
          : "shadow-2xl",
        isPanelVisible ? "translate-x-0" : "translate-x-full"
      )}
      style={{
        top: 0,
        width: isDesktop
          ? SCHEDULE_DETAIL_SIDEBAR_WIDTH_PX
          : "min(420px, 90vw)",
        zIndex: SCHEDULE_DETAIL_SIDEBAR_Z_INDEX,
        transitionDuration: `${SCHEDULE_DETAIL_TRANSITION_MS}ms`,
      }}
      aria-label="Detalhes da visita"
      role="dialog"
      aria-modal={!isDesktop}
    >
      <header className="sticky top-0 z-10 shrink-0 border-b border-[#EDF2F7] bg-white px-4 pb-4 pt-5">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-5 rounded-lg p-2 text-[#718096] hover:bg-[#F7FAFC]"
          aria-label="Fechar painel"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>

        <div className="min-w-0 pr-12">
          {currentAppointment ? (
            <>
              <p className="text-[11px] font-bold uppercase leading-snug tracking-[0.06em] text-[#4A5568]">
                {formatScheduleDetailHeader(
                  currentAppointment.schedule.date,
                  currentAppointment.consultant.first_name
                )}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {currentAppointment.video_call ? (
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-sky-800">
                    <Video className="h-4 w-4" aria-hidden />
                    Visita vídeo
                  </span>
                ) : (
                  <span className="text-sm font-semibold text-[#2D3748]">
                    Visita presencial
                  </span>
                )}
                <VisitCardCreatedBadge status={cardStatus} />
              </div>
            </>
          ) : (
            <p className="text-sm font-semibold text-[#2D3748]">
              Detalhes da visita
            </p>
          )}
        </div>

        {weatherHint ? (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-sky-100 bg-sky-50 px-3 py-2 text-xs font-medium text-sky-900">
            <CloudSun className="h-4 w-4 shrink-0" aria-hidden />
            {weatherHint}
          </div>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {isFetching ? (
          <ModalSkeleton />
        ) : !currentAppointment ? (
          <p className="text-sm text-[#718096]">
            Nenhum agendamento encontrado para este horário.
          </p>
        ) : (
          <ScheduleDetailPanelBody
            appointment={currentAppointment}
            canManageSchedules={canManageSchedules}
            clientPagination={clientPagination}
          />
        )}
      </div>

      {currentAppointment && canManageSchedules ? (
        <footer className="shrink-0 border-t border-[#EDF2F7] bg-white px-3 py-3">
          {!currentAppointment.outcome &&
          isVisitDue(currentAppointment.schedule.date) ? (
            <div className="mb-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                disabled={savingOutcome !== null}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#087443] px-3 py-2 text-sm font-semibold text-white hover:bg-[#05613A] disabled:opacity-50"
                onClick={() => handleOutcome("completed")}
              >
                <Check className="h-4 w-4" aria-hidden />
                Realizada
              </button>
              <button
                type="button"
                disabled={savingOutcome !== null}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#F6AD55] bg-[#FFFAF0] px-3 py-2 text-sm font-semibold text-[#9C4221] hover:bg-[#FEEBC8] disabled:opacity-50"
                onClick={() => handleOutcome("no_show")}
              >
                <UserX className="h-4 w-4" aria-hidden />
                Não compareceu
              </button>
            </div>
          ) : null}
          {onScheduleAnother ? (
            <button
              type="button"
              disabled={!canScheduleAnotherVisitInSlot(appointments.length)}
              title={
                canScheduleAnotherVisitInSlot(appointments.length)
                  ? "Mesmo imóvel e horário: o consultor não pode estar em dois lugares."
                  : undefined
              }
              className="mb-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-[#3182CE]/50 bg-[#F5F9FF] px-3 py-2 text-sm font-semibold text-[#2B6CB0] hover:bg-[#EBF4FF] disabled:cursor-not-allowed disabled:border-[#E2E8F0] disabled:bg-[#F7FAFC] disabled:text-[#A0AEC0]"
              onClick={() =>
                onScheduleAnother(
                  scheduleParam,
                  resolveAppointmentPropertyId(currentAppointment.property)
                )
              }
            >
              <UserPlus className="h-4 w-4" aria-hidden />
              {canScheduleAnotherVisitInSlot(appointments.length)
                ? `Agendar outro cliente neste horário (${appointments.length}/${ADM_MAX_VISITS_PER_SLOT})`
                : `Limite de ${ADM_MAX_VISITS_PER_SLOT} visitas neste horário`}
            </button>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="min-w-[30%] flex-1 rounded-lg border border-[#E2E8F0] px-3 py-2 text-sm font-semibold text-[#4A5568] hover:bg-[#F7FAFC]"
              onClick={() =>
                onDemarcate(
                  currentAppointment.schedule.id,
                  currentAppointment.schedule.date
                )
              }
            >
              Desmarcar
            </button>
            <VisitCreateCardButton
              scheduleId={currentAppointment.schedule.id}
              canCreateCard={cardStatus.can_create_card}
              onCreated={(nextStatus) => setCardStatusOverride(nextStatus)}
              className="min-w-[30%] flex-1 rounded-lg border border-[#c4552b]/30 bg-[#FFF5F0] px-3 py-2 text-sm font-semibold text-[#c4552b] hover:bg-[#FFEDE4] disabled:opacity-50"
            />
            <button
              type="button"
              className="min-w-[30%] flex-1 rounded-lg bg-[#dd6435] px-3 py-2 text-sm font-semibold text-white hover:bg-[#c4552b]"
              onClick={() =>
                onUpdate(
                  currentAppointment.schedule.id,
                  scheduleParam,
                  !!currentAppointment.video_call
                )
              }
            >
              Alterar
            </button>
          </div>
        </footer>
      ) : null}
    </aside>
  );

  return createPortal(
    <>
      {!isDesktop ? (
        <button
          type="button"
          className="fixed inset-0 bg-black/40"
          style={{ zIndex: SCHEDULE_DETAIL_BACKDROP_Z_INDEX }}
          aria-label="Fechar painel"
          onClick={onClose}
        />
      ) : null}
      {panel}
    </>,
    document.body
  );
};

export default ScheduleDetailPanel;
